<template>
  <div class="container mx-auto h-[calc(85vh-220px)] md:h-[calc(85vh-320px)] lg:h-[calc(85vh-420px)] z-0 overflow-hidden flex flex-col relative">
    <div
      class="flex-1 z-0 overflow-hidden border-4 border-l-2 border-gray-300 rounded-lg shadow-lg bg-gray-100"
    >
      <TargetAreaShow
        v-if="hasTargetArea && apiToken"
        :target-area-external-id="targetAreaExternalId"
        :api-base-url="sparrowBaseUrl"
        :api-key="apiToken"
        :village-radius-km="50"
        :training-center-lat="tcLat"
        :training-center-lng="tcLng"
        :show-village-list="false"
        :on-token-refresh="handleTokenRefresh"
        :on-auth-failure="handleAuthFailure"
      />
    </div>

    <!-- Overlay for when no target area exists and target areas are enabled -->
    <div
      v-if="!hasTargetArea && targetAreasEnabled"
      class="absolute inset-0 bg-black bg-opacity-50 flex items-end z-10 rounded-lg"
    >
      <div class="mb-2 rounded-lg shadow-xl text-center mx-auto">
        <button
          @click="navigateToTargetArea"
          class="w-full bg-gray-200 hover:bg-white text-black font-medium text-sm py-1 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          <span>{{ t('modals.kestrel_map.target_area') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, computed } from 'vue'
  import { TargetAreaShow } from '@ishare/kestrel-vue'
  import '@ishare/kestrel-vue/style.css'
  import 'maplibre-gl/dist/maplibre-gl.css'
  import i18n from '@utils/i18n'
  import FlashMessageService from '@services/FlashMessages'
  import { useSparrowAuth } from '@composables/useSparrowAuth'

  const { t } = i18n.global

  const props = defineProps({
    staticKestrelMapData: Array,
  })

  const firstEntry = computed(() => props.staticKestrelMapData?.[0] || {})
  const targetAreaExternalId = computed(() => firstEntry.value.target_area_external_id)
  const hasTargetArea = computed(() => targetAreaExternalId.value != null)
  const targetAreasEnabled = computed(() => firstEntry.value.target_areas_enabled === true)
  const countryIso3 = computed(() => firstEntry.value.country_iso3_code)
  const tcLat = computed(() => firstEntry.value.lat != null ? parseFloat(firstEntry.value.lat) : undefined)
  const tcLng = computed(() => firstEntry.value.long != null ? parseFloat(firstEntry.value.long) : undefined)

  // kestrel-vue v2 appends /api/v2/... internally, so strip the trailing /api
  const sparrowBaseUrl = computed(() => (firstEntry.value.kestrel_api_url || '').replace(/\/api\/?$/, ''))

  const { token: apiToken, fetchToken: fetchConstrainedToken, handleTokenRefresh, handleAuthFailure } = useSparrowAuth({
    countryIso3,
    capabilities: ['target_area.view', 'boundary.view:all', 'boundary.view_shading:all'],
    onError: () => FlashMessageService.error(t('modals.kestrel_map.error_messages.initializing_map')),
  })

  function navigateToTargetArea() {
    const trainingCenterId = firstEntry.value.training_center_id
    if (trainingCenterId) {
      window.location.href = `/training_centers/${trainingCenterId}/target_area`
    }
  }

  onMounted(async () => {
    if (!hasTargetArea.value) return

    try {
      await fetchConstrainedToken()
    } catch (err) {
      console.error('Failed to fetch constrained token for static preview map:', err)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.initializing_map'))
    }
  })
</script>

<style scoped>
</style>
